import { create } from 'zustand';

export type ThemePref = 'light' | 'dark' | 'system';
const STORAGE_KEY = 'fu_driver_theme';

type ThemeState = {
  pref: ThemePref;
  effective: 'light' | 'dark';  // resolved against system if pref === 'system'
  setPref: (p: ThemePref) => void;
  init: () => void;
};

function detectSystem(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyToDocument(effective: 'light' | 'dark', animate = false) {
  const el = document.documentElement;
  if (animate) {
    // Transition only during an explicit toggle (not on initial load, which
    // would flash-animate every element). `.theme-transition` scopes the CSS
    // transition in globals.css; remove it once the animation settles.
    el.classList.add('theme-transition');
    window.setTimeout(() => el.classList.remove('theme-transition'), 320);
  }
  el.dataset.theme = effective;
}

export const useTheme = create<ThemeState>((set, get) => ({
  pref: 'system',
  effective: 'light',
  setPref: (p) => {
    localStorage.setItem(STORAGE_KEY, p);
    const effective = p === 'system' ? detectSystem() : p;
    applyToDocument(effective, true);
    set({ pref: p, effective });
  },
  init: () => {
    const stored = (localStorage.getItem(STORAGE_KEY) as ThemePref) || 'system';
    const effective = stored === 'system' ? detectSystem() : stored;
    applyToDocument(effective);
    set({ pref: stored, effective });

    // Listen for system pref changes (only when pref === 'system')
    if (typeof window !== 'undefined' && window.matchMedia) {
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
        if (get().pref !== 'system') return;
        const eff = e.matches ? 'dark' : 'light';
        applyToDocument(eff, true);
        set({ effective: eff });
      });
    }
  },
}));
